import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Result } from 'bloggers-backend/lib/domain/Result';
import { ScheduledEmail } from 'bloggers-backend/lib/emailScheduler/domain';
import { AppContext } from '../../../AppContext';

interface EnqueueEmailDialogProps {
  scheduleId: string;
}

// A `datetime-local` input value ("YYYY-MM-DDTHH:mm") for the given moment, in the browser's
// local timezone. Used to seed the picker with "now".
function toLocalInputValue(date: Date): string {
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

export function EnqueueEmailDialog({ scheduleId }: EnqueueEmailDialogProps) {
  const { client, notificationsService } = React.useContext(AppContext);
  const queryClient = useQueryClient();

  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [to, setTo] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [text, setText] = React.useState('');
  // Local wall-clock time picked by the user; converted to an ISO UTC instant on submit.
  const [scheduledAt, setScheduledAt] = React.useState(() => toLocalInputValue(new Date()));

  const close = () => {
    setOpen(false);
    setTo('');
    setSubject('');
    setText('');
    setScheduledAt(toLocalInputValue(new Date()));
  };

  const submit = async () => {
    setSubmitting(true);
    const result = await client<{}, Result<ScheduledEmail>>({
      event: 'emailScheduler.enqueue',
      data: { scheduleId, to, subject, text, scheduledAt: new Date(scheduledAt).toISOString() },
    });
    setSubmitting(false);

    if (result.success) {
      notificationsService.notify({ severity: 'success', message: 'Email enqueued' });
      // Refresh every status view of this schedule's queue.
      await queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === 'string' &&
          (query.queryKey[0] as string).startsWith(`scheduledEmails-${scheduleId}`),
      });
      close();
    } else {
      notificationsService.notify({
        severity: 'error',
        message: result.reason === 'validation' ? JSON.stringify(result.errors) : result.reason,
      });
    }
  };

  return (
    <>
      <Button variant="contained" onClick={() => setOpen(true)}>
        Add email
      </Button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>Enqueue email</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="to" name="to" value={to} onChange={(e) => setTo(e.target.value)} fullWidth />
            <TextField
              label="subject"
              name="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
            />
            <TextField
              label="text"
              name="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              fullWidth
              multiline
              minRows={4}
            />
            <TextField
              label="scheduled at"
              name="scheduledAt"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={submitting}>
            Enqueue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
